import React, { useEffect, useState } from 'react'
import { Heading, Text, Button } from '@primer/react-brand'

import CurrentCardPanel from '../components/CurrentCardPanel'

// UC-9 — Payment method updated (success screen). Landed on after a successful
// card update (UpdatePaymentMethodPage). Reuses the same react-brand Heading/Text/
// Button pieces and the shared CurrentCardPanel from the update screen. The green
// "Outstanding balance charged" panel only appears when the update actually
// charged the card (approved-but-unpaid or past_due); a plain card swap shows just
// the confirmation. The charge summary is handed over via sessionStorage so it
// survives the redirect without living in the URL.
//
// Props: { currentCard: { brand, last4, expMonth, expYear } | null, backPath }

const CHARGE_STASH_KEY = 'cardUpdateCharge'

const formatAmount = (cents, currency) => {
  if (cents == null) return null
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
    }).format(cents / 100)
  } catch (e) {
    return `$${(cents / 100).toFixed(2)}`
  }
}

const formatDate = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const PaymentMethodUpdatedPage = ({ currentCard, backPath = '/monitoring' }) => {
  // Read (and clear) the one-shot charge summary the update screen stashed, so a
  // refresh doesn't re-show a charge that isn't happening again.
  const [charge, setCharge] = useState(null)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(CHARGE_STASH_KEY)
      sessionStorage.removeItem(CHARGE_STASH_KEY)
      if (raw) setCharge(JSON.parse(raw))
    } catch (e) { /* no charge summary — show the plain confirmation */ }
  }, [])

  const amount = charge && formatAmount(charge.amountCents, charge.currency)
  const nextChargeDate = charge && formatDate(charge.nextChargeAt)
  const nextChargeSuffix = nextChargeDate ? ` — next charge ${nextChargeDate}.` : '.'

  // Copy differs by which failed-payment balance was charged:
  //   'initial'  — the approval-time first charge had been declined; it's now paid
  //                and the subscription starts for the first time.
  //   'past_due' — a recurring renewal had failed; the overdue invoice is now paid
  //                and the subscription is current again.
  const isInitial = charge && charge.kind === 'initial'
  const panelTitle = isInitial ? 'Payment charged' : 'Outstanding balance charged'
  const panelBody = isInitial
    ? `We charged your first payment to this card. Your subscription is now active${nextChargeSuffix}`
    : `We charged your past-due invoice to this card. Your subscription is active again${nextChargeSuffix}`

  return (
    <div style={{ maxWidth: 480, width: '100%', margin: '0 auto', padding: '52px 40px 96px', fontFamily: 'var(--brand-body-fontFamily)' }}>
      <Heading as="h1" size="5">Payment method updated</Heading>
      <Text as="p" size="300" variant="muted" style={{ marginTop: 6, marginBottom: 28, lineHeight: 1.65 }}>
        Your new card is saved and set as your default payment method.
      </Text>

      <CurrentCardPanel card={currentCard} />

      {charge && (
        <div
          style={{
            border: '1px solid var(--base-color-scale-green-1)',
            background: 'var(--base-color-scale-green-0)',
            borderRadius: 8,
            padding: '14px 16px',
            marginBottom: 24,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--base-color-scale-gray-9)' }}>
              {panelTitle}
            </span>
            {amount && (
              <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--base-color-scale-gray-9)' }}>
                {amount}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--base-color-scale-gray-6)', lineHeight: 1.5, marginTop: 6 }}>
            {panelBody}
          </div>
        </div>
      )}

      <Button
        as="a"
        href={backPath}
        variant="primary"
        style={{
          backgroundColor: 'var(--base-color-scale-blue-5)',
          borderColor: 'var(--base-color-scale-blue-5)',
          borderRadius: '6px',
        }}
      >
        Back to dashboard
      </Button>
    </div>
  )
}

export default PaymentMethodUpdatedPage
